import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "./useSession";
import { useViewMode } from "./useViewMode";

export interface ComposeDefaults {
  kind?: "message" | "tache" | "annonce";
  boutiqueIds?: string[];
  title?: string;
  description?: string;
}

export interface ProductSheetTarget {
  /** Identifiant interne du produit (produit réseau) ; null tant qu'il n'est pas rattaché. */
  id: string | null;
  /** Nom affiché en attendant la fiche (et repli quand l'identifiant manque). */
  name: string;
  /** « reseau » : ventes et stock du périmètre ; « boutique » : focus sur une boutique. */
  scope: "reseau" | "boutique";
  boutiqueId?: string;
}

interface UiState {
  compose: ComposeDefaults | null;
  openCompose: (d?: ComposeDefaults) => void;
  closeCompose: () => void;
  product: ProductSheetTarget | null;
  openProduct: (p: ProductSheetTarget) => void;
  closeProduct: () => void;
}

const Ctx = createContext<UiState | null>(null);

const TAB_LABELS: Record<string, string> = { stock: "Stock", produits: "Stock", arrivages: "Arrivages", ventes: "Ventes" };

/**
 * Panneaux globaux : « Nouveau message / tâche » et fiche produit. Sur PC la
 * fiche produit est une page (/produits/fiche) avec un retour vers l'écran
 * d'origine ; sur mobile, un panneau.
 */
export function UiProvider({ children }: { children: ReactNode }) {
  const [compose, setCompose] = useState<ComposeDefaults | null>(null);
  const [product, setProduct] = useState<ProductSheetTarget | null>(null);
  const mode = useViewMode();
  const navigate = useNavigate();
  const loc = useLocation();
  const { me } = useSession();
  const openProduct = useCallback(
    (p: ProductSheetTarget) => {
      if (mode === "mobile") return setProduct(p);
      const tab = new URLSearchParams(loc.search).get("tab");
      const boutique = loc.pathname.match(/^\/boutiques\/([^/]+)/)?.[1];
      const city = me.boutiques.find((b) => b.id === boutique)?.city;
      const label = city
        ? `${city} · ${TAB_LABELS[tab ?? ""] ?? "Aperçu"}`
        : loc.pathname.startsWith("/produits")
          ? "Produits"
          : loc.pathname === "/"
            ? (TAB_LABELS[tab ?? ""] ?? "Accueil")
            : "Retour";
      const q = new URLSearchParams(p.id ? { id: p.id } : { nom: p.name });
      if (p.scope === "boutique" && p.boutiqueId) q.set("boutique", p.boutiqueId);
      navigate(`/produits/fiche?${q}`, { state: { back: { label, to: loc.pathname + loc.search } } });
    },
    [mode, navigate, loc.pathname, loc.search, me.boutiques]
  );
  const value = useMemo(
    () => ({
      compose,
      openCompose: (d: ComposeDefaults = {}) => setCompose(d),
      closeCompose: () => setCompose(null),
      product,
      openProduct,
      closeProduct: () => setProduct(null),
    }),
    [compose, product, openProduct]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUi(): UiState {
  const v = useContext(Ctx);
  if (!v) throw new Error("UiProvider manquant");
  return v;
}
